import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ExitButtonComponent } from '../../../../shared/components/buttons/exit-button/exit-button.component';
import { SnackbarComponent } from '@predigle/micro/component-ui';
import { TripService } from '../../../../services/trip.service';
import { WaypointService } from '../../../../services/waypoint.service';
import { SocialService } from '../../../../services/social.service';
import { AuthService } from '../../../../core/auth/auth.service';
import {
  FLEET_RECIPIENT_OFFSET,
  TripRestrictedFleetRef,
  RestrictiveFleet,
  toggledRestrictiveRecipientSelection,
  isRestrictiveRecipientDisabled,
  isRestrictiveAsMember,
  hasRestrictiveFleetSelection,
  normalizeRestrictiveRecipientSelection,
} from '../../../../shared/utils/restrictive-sharing.utils';

interface FleetItem extends RestrictiveFleet {
  name: string;
  image_url?: string;
  membersCount: number;
}

interface DockItem {
  id: string;
  name: string;
  image_url?: string;
  vesselName?: string;
}

@Component({
  selector: 'app-visibility-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, ExitButtonComponent, SnackbarComponent],
  templateUrl: './visibility-settings.component.html',
  styleUrl: './visibility-settings.component.scss'
})
export class VisibilitySettingsComponent implements OnInit {
  @Input() tripId = '';
  @Output() close = new EventEmitter<void>();
  @Output() update = new EventEmitter<void>();

  selectedPrivacy = 'private';
  visibilitySearchQuery = '';
  loadingRecipients = false;
  saving = false;

  fleets: FleetItem[] = [];
  dockmates: DockItem[] = [];
  selectedFollowers = new Set<number>();

  // IDs already shared via backend (locked, cannot unselect)
  alreadySharedFleetIds = new Set<string>();
  alreadySharedFriendIds = new Set<string>();

  // If backend says this trip is locked to a specific restrictive fleet
  restrictedFleetRef: TripRestrictedFleetRef | null = null;

  // True when a restrictive fleet is already in the backend shares
  hasAlreadySharedRestrictiveFleet = false;

  showSnackbar = false;
  snackbarMessage = '';
  snackbarType: 'success' | 'error' | 'warning' = 'success';

  readonly FLEET_OFFSET = FLEET_RECIPIENT_OFFSET;

  constructor(
    private tripService: TripService,
    private waypointService: WaypointService,
    private socialService: SocialService,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.loadTripData();
  }

  // ─── Key helpers ──────────────────────────────────────────────
  fleetKeyIndex(fleetArrayIndex: number): number {
    return FLEET_RECIPIENT_OFFSET + fleetArrayIndex;
  }

  dmKeyIndex(dmArrayIndex: number): number {
    return dmArrayIndex;
  }

  isChecked(keyIndex: number): boolean {
    return this.selectedFollowers.has(keyIndex);
  }

  isAlreadySharedFleet(fleet: FleetItem): boolean {
    return this.alreadySharedFleetIds.has(fleet.id);
  }

  isAlreadySharedDock(dm: DockItem): boolean {
    return this.alreadySharedFriendIds.has(dm.id);
  }

  // Used by click handlers — blocks interaction on already-shared AND restrictive-locked items
  isDisabled(keyIndex: number, alreadyShared: boolean): boolean {
    if (alreadyShared) return true;
    if (this.hasAlreadySharedRestrictiveFleet) return true;
    return isRestrictiveRecipientDisabled(
      keyIndex,
      this.isChecked(keyIndex),
      this.selectedFollowers,
      this.fleets,
    );
  }

  // Used for CSS gray-disabled styling ONLY — already-shared items stay blue/checked
  isRestrictiveLocked(keyIndex: number, alreadyShared: boolean): boolean {
    if (alreadyShared) return false;
    if (this.hasAlreadySharedRestrictiveFleet) return true;
    return isRestrictiveRecipientDisabled(
      keyIndex,
      this.isChecked(keyIndex),
      this.selectedFollowers,
      this.fleets,
    );
  }

  get disableEveryoneOption(): boolean {
    // Mirrors Flutter: _loadedTrip?.hasRestrictedFleetRef == true
    // A non-null restrictedFleetRef means the backend has locked this trip to a
    // restrictive fleet — public is permanently unavailable.
    return !!this.restrictedFleetRef ||
      this.hasAlreadySharedRestrictiveFleet ||
      hasRestrictiveFleetSelection(this.selectedFollowers, this.fleets);
  }

  onToggle(keyIndex: number, alreadyShared: boolean) {
    if (alreadyShared) return;
    if (this.hasAlreadySharedRestrictiveFleet) return;
    this.selectedFollowers = toggledRestrictiveRecipientSelection(
      this.selectedFollowers,
      keyIndex,
      this.fleets,
    );
  }

  // ─── Filtering ────────────────────────────────────────────────
  get filteredFleets(): FleetItem[] {
    const q = this.visibilitySearchQuery.trim().toLowerCase();
    if (!q) return this.fleets;
    return this.fleets.filter(f => f.name?.toLowerCase().includes(q));
  }

  get filteredDockmates(): DockItem[] {
    const q = this.visibilitySearchQuery.trim().toLowerCase();
    if (!q) return this.dockmates;
    return this.dockmates.filter(m => m.name?.toLowerCase().includes(q));
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  // ─── Load trip data + shared_with ─────────────────────────────
  private loadTripData() {
    if (!this.tripId) return;

    // Load trip details to get current privacy + restrictedFleetRef
    this.tripService.getTripById(this.tripId).subscribe({
      next: (trip: any) => {
        const privacy = trip?.privacy || 'private';
        if (privacy === 'public') this.selectedPrivacy = 'public';
        else if (privacy === 'custom' || privacy === 'friends') this.selectedPrivacy = 'dockmates';
        else this.selectedPrivacy = 'private';

        const ref = trip?.restricted_fleet_ref;
        if (ref?.identifier?.trim()) {
          this.restrictedFleetRef = {
            identifier: ref.identifier,
            name: ref.name || 'Restricted Fleet',
            type: ref.type,
            hidden: ref.hidden ?? false,
            deletedStatus: ref.deleted_status,
          };
        }

        // Load shared_with to know who is already shared
        this.tripService.getTripSharedWith(this.tripId).subscribe({
          next: (sharedRes: any) => {
            const shared = sharedRes?.shared_with || sharedRes || {};
            const fleetItems: any[] = shared.fleets || shared.fleet || [];
            const friendItems: any[] = shared.friends || shared.friend || [];

            const freshFleetIds: string[] = [];
            const freshFriendIds: string[] = [];

            fleetItems.forEach((f: any) => {
              const id = f.id || f._id || f.community_id || f.fleet_id || f.identifier || '';
              if (id) freshFleetIds.push(id);
            });
            friendItems.forEach((f: any) => {
              const id = f.id || f._id || f.user_id || f.friend_id || f.identifier || '';
              if (id) freshFriendIds.push(id);
            });

            // If API returned data, update cache; if empty (privacy=private wipes response),
            // restore from cache so switching back to dockmates shows previous shares.
            if (freshFleetIds.length > 0 || freshFriendIds.length > 0) {
              this.tripService.cacheSharedWith(this.tripId, freshFleetIds, freshFriendIds);
              freshFleetIds.forEach(id => this.alreadySharedFleetIds.add(id));
              freshFriendIds.forEach(id => this.alreadySharedFriendIds.add(id));
            } else {
              const cached = this.tripService.getCachedSharedWith(this.tripId);
              cached.fleetIds.forEach(id => this.alreadySharedFleetIds.add(id));
              cached.friendIds.forEach(id => this.alreadySharedFriendIds.add(id));
            }

            this.loadRecipients();
          },
          error: () => {
            // On error, restore from cache so history isn't lost
            const cached = this.tripService.getCachedSharedWith(this.tripId);
            cached.fleetIds.forEach(id => this.alreadySharedFleetIds.add(id));
            cached.friendIds.forEach(id => this.alreadySharedFriendIds.add(id));
            this.loadRecipients();
          },
        });
      },
      error: () => this.loadRecipients(),
    });
  }

  private loadRecipients() {
    this.loadingRecipients = true;
    const tokenUserId = this.authService.getUserIdFromToken();
    const objUserId = this.authService.getUserObjectId();

    if (this.restrictedFleetRef?.identifier) {
      // Locked trip: only load the one restricted fleet
      this.socialService.getCommunityById(this.restrictedFleetRef.identifier).subscribe({
        next: (fleet: any) => {
          const item: FleetItem = {
            id: fleet.id || fleet._id || this.restrictedFleetRef!.identifier,
            name: fleet.name || this.restrictedFleetRef!.name,
            image_url: fleet.image_url || fleet.image || '',
            membersCount: fleet.members_count ?? 0,
            isRestrictive: true,
          };
          this.fleets = [item];
          this.dockmates = [];
          this.reverseMapAndNormalize();
          this.loadingRecipients = false;
        },
        error: () => {
          // Fallback: build a minimal fleet from the ref
          this.fleets = [{
            id: this.restrictedFleetRef!.identifier,
            name: this.restrictedFleetRef!.name,
            membersCount: 0,
            isRestrictive: true,
          }];
          this.dockmates = [];
          this.reverseMapAndNormalize();
          this.loadingRecipients = false;
        },
      });
    } else {
      // Normal trip: load all my fleets + friends
      this.waypointService.getFleets().subscribe({
        next: (res) => {
          this.fleets = (res.results || []).map((f: any) => ({
            id: f.id || f._id || f.community_id || '',
            name: f.name || '',
            image_url: f.image_url || f.image || '',
            membersCount: f.members_count ?? f.membersCount ?? 0,
            isRestrictive: f.is_restrictive ?? f.isRestrictive ?? false,
            myRole: f.my_role ?? f.myRole ?? '',
          }));

          this.socialService.myDockmates(1, 100).subscribe({
            next: (dmRes) => {
              const results = (dmRes.results || []).filter((m: any) => {
                const ids = [m.user?._id, m.user?.user_id];
                return !ids.includes(tokenUserId) && !ids.includes(objUserId);
              });
              this.dockmates = results.map((m: any) => ({
                id: m.user?._id || '',
                name: m.user?.name || '',
                image_url: m.user?.image_url || '',
                vesselName: m.vessel_name || '',
              }));

              this.reverseMapAndNormalize();
              this.loadingRecipients = false;
            },
            error: () => { this.loadingRecipients = false; },
          });
        },
        error: () => { this.loadingRecipients = false; },
      });
    }
  }

  private reverseMapAndNormalize() {
    const mapped = new Set<number>();

    this.fleets.forEach((fleet, i) => {
      if (this.alreadySharedFleetIds.has(fleet.id)) {
        mapped.add(this.fleetKeyIndex(i));
      }
    });

    this.dockmates.forEach((dm, i) => {
      if (this.alreadySharedFriendIds.has(dm.id)) {
        mapped.add(this.dmKeyIndex(i));
      }
    });

    this.selectedFollowers = normalizeRestrictiveRecipientSelection(mapped, this.fleets);

    // Check if any already-shared fleet is restrictive (and user is not owner/admin) → lock everyone else
    this.hasAlreadySharedRestrictiveFleet = this.fleets.some(
      f => isRestrictiveAsMember(f) && this.alreadySharedFleetIds.has(f.id)
    );

    // If restrictive fleet is locked (via trip field or shared fleet), force off public
    if ((this.hasAlreadySharedRestrictiveFleet || !!this.restrictedFleetRef) && this.selectedPrivacy === 'public') {
      this.selectedPrivacy = 'dockmates';
    }
  }

  selectPrivacy(option: string) {
    if (option === 'public' && this.disableEveryoneOption) return;
    this.selectedPrivacy = option;
    if (option === 'dockmates' && this.fleets.length === 0 && !this.loadingRecipients) {
      this.loadRecipients();
    }
  }

  // ─── Build share payload ───────────────────────────────────────
  private buildAudiencePayload(): { friend_ids: string[]; fleet_ids: string[] } {
    const fleet_ids = new Set<string>(this.alreadySharedFleetIds);
    const friend_ids = new Set<string>(this.alreadySharedFriendIds);

    for (const keyIndex of this.selectedFollowers) {
      if (keyIndex >= FLEET_RECIPIENT_OFFSET) {
        const i = keyIndex - FLEET_RECIPIENT_OFFSET;
        if (i >= 0 && i < this.fleets.length && this.fleets[i].id) {
          fleet_ids.add(this.fleets[i].id);
        }
      } else {
        const i = keyIndex;
        if (i >= 0 && i < this.dockmates.length && this.dockmates[i].id) {
          friend_ids.add(this.dockmates[i].id);
        }
      }
    }

    return {
      fleet_ids: Array.from(fleet_ids),
      friend_ids: Array.from(friend_ids),
    };
  }

  // ─── Actions ──────────────────────────────────────────────────
  onCancel() {
    this.close.emit();
  }

  onUpdate() {
    if (!this.tripId || this.saving) return;
    this.saving = true;

    const { friend_ids, fleet_ids } = this.buildAudiencePayload();

    const privacyMap: Record<string, string> = {
      public: 'public',
      private: 'private',
      dockmates: 'custom',
    };
    const apiPrivacy = privacyMap[this.selectedPrivacy] || 'private';

    // Update privacy first, then share
    this.tripService.updateTrip(this.tripId, { privacy: apiPrivacy }).subscribe({
      next: () => {
        if (this.selectedPrivacy === 'dockmates' && (friend_ids.length > 0 || fleet_ids.length > 0)) {
          this.tripService.shareTrip(this.tripId, { friend_ids, fleet_ids, message: ' ' }).subscribe({
            next: () => {
              // Update cache with the latest shared IDs so history survives re-open
              this.tripService.cacheSharedWith(this.tripId, fleet_ids, friend_ids);
              this.saving = false;
              this.showSuccessMsg('Visibility updated');
              setTimeout(() => this.update.emit(), 1200);
            },
            error: () => {
              this.saving = false;
              this.showError('Privacy updated but sharing failed');
              setTimeout(() => this.update.emit(), 1200);
            },
          });
        } else {
          this.saving = false;
          this.showSuccessMsg('Visibility updated');
          setTimeout(() => this.update.emit(), 1200);
        }
      },
      error: () => {
        this.saving = false;
        this.showError('Failed to update visibility');
      },
    });
  }

  // ─── Snackbar ─────────────────────────────────────────────────
  private showSuccessMsg(msg: string) {
    this.snackbarMessage = msg;
    this.snackbarType = 'success';
    this.showSnackbar = true;
  }

  private showError(msg: string) {
    this.snackbarMessage = msg;
    this.snackbarType = 'error';
    this.showSnackbar = true;
  }

  onSnackbarClose() { this.showSnackbar = false; }
  onSnackbarAutoClose() { this.showSnackbar = false; }
}
